import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enTranslation from "./i18n/locales/en/translation.json";

const SUPPORTED_LANGUAGES = ["en", "es"];
let spanishTranslationPromise;

const getInitialLanguage = () => {
  try {
    const storedLanguage = window.localStorage.getItem("cf-lang");
    if (SUPPORTED_LANGUAGES.includes(storedLanguage)) return storedLanguage;
  } catch {
    // Continue with browser-language detection when storage is unavailable.
  }

  const browserLanguages = navigator.languages || [navigator.language];
  return browserLanguages.some((language) => language?.toLowerCase().startsWith("es"))
    ? "es"
    : "en";
};

const initialLanguage = getInitialLanguage();

const loadSpanishTranslation = () => {
  if (!spanishTranslationPromise) {
    spanishTranslationPromise = import("./i18n/locales/es/translation.json").then(
      (module) => module.default,
    );
  }

  return spanishTranslationPromise;
};

const getResourcesForLanguage = async (language) => {
  const resources = { en: { translation: enTranslation } };

  if (language === "es") {
    resources.es = { translation: await loadSpanishTranslation() };
  }

  return resources;
};

export const i18nReady = getResourcesForLanguage(initialLanguage)
  .then((resources) => i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLanguage,
    fallbackLng: "en",
    supportedLngs: ["en", "es"],
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  }))
  .then(() => {
    try {
      window.localStorage.setItem("cf-lang", initialLanguage);
    } catch {
      // Language selection remains available when storage is unavailable.
    }
  });

export const setLanguage = async (language) => {
  const nextLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : "en";
  await i18nReady;

  if (nextLanguage === "es" && !i18n.hasResourceBundle("es", "translation")) {
    i18n.addResourceBundle("es", "translation", await loadSpanishTranslation());
  }

  try {
    window.localStorage.setItem("cf-lang", nextLanguage);
  } catch {
    // Language selection remains available when storage is unavailable.
  }

  return i18n.changeLanguage(nextLanguage);
};

export default i18n;
